/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';

import Evolution from '../../components/Evolution/Evolution';
import LineChart from '../../components/LineChart/LineChart';
import EuaCertificatesTable from './EuaCertificatesTable/EuaCertificatesTable';
import Loader from '../../components/Loader/Loader';
import Toggler from '../../components/Toggler/Toggler';
import Asterix, { AsterixType } from '../../components/Asterix/Asterix';

import oilCoalEvolution from '../../core/data/oil-coal/evolution';
import OilCoalService from '../../core/services/oil-coal-service';
import CommentsApiService from '../../core/services/comments-api-service';
import COLORS from '../../core/constants/colors';
import { OilCoalModel } from '../../core/models/oil-coal';

import './OilCoalSection.scss';
import ChartHints from '../../components/ChartHints/ChartHints';

const apiService = new OilCoalService();
const commentsService = new CommentsApiService('');

const togglerState = {
  euro: '€/tonne',
  pound: '£/tonne',
};

export default function OilCoalSection(): JSX.Element {
  const [oilCoalData, setOilCoalData] = useState<OilCoalModel>({} as OilCoalModel);
  const [isLoading, setIsLoading] = useState(true);
  const [carbonUnits, setCarbonUnits] = useState<string>(togglerState.euro);

  useEffect(() => {
    apiService.getOilCoal().then((response) => {
      setOilCoalData(response);
      setIsLoading(false);
    });
  }, []);

  const onToggle = (value: string): void => {
    setCarbonUnits(value);
  };

  const onSave = (data: { value: string; id: number; type: string }) => {
    const sanitizeConf = {
      allowedTags: [],
      allowedAttributes: {},
    };
    const value = sanitizeHtml(data.value, sanitizeConf);
    if (data.id) {
      return commentsService.updateCustomPrice(data.id, data.type, value);
    }
    return commentsService.setCustomPrice(data.type, value).then((response: any) => {
      setOilCoalData({
        ...oilCoalData,
        euaPricesTable: {
          ...oilCoalData.euaPricesTable,
          pricesTable: oilCoalData.euaPricesTable.pricesTable.map((item) => {
            if (item.postIndex === data.type) {
              return {
                ...item,
                idUpdate: response.id,
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onRemoveCustomPrice = (id: number) => {
    return commentsService.removeCustomPrice(id).then(() => {
      setOilCoalData({
        ...oilCoalData,
        euaPricesTable: {
          ...oilCoalData.euaPricesTable,
          pricesTable: oilCoalData.euaPricesTable.pricesTable.map((item) => {
            if (item.idUpdate === id) {
              return {
                ...item,
                idUpdate: null,
                col4: Number(item.col4),
              };
            }
            return item;
          }),
        },
      });
    });
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className="OilCoalSection" id="brent-oil">
      <div className="total-container">
        <div className="row">
          <div className="col-md-6 mb-10">
            <h2>
              <span>BRENT OIL</span>
            </h2>
            <div className="OilCoalSection__content">
              <div className="section-summary mb-4 pl-2-8">
                <Evolution
                  arrow={oilCoalData.brentOil.arrow}
                  price={oilCoalData.brentOil.value}
                  origin={oilCoalEvolution.brentOil.origin}
                  currency={oilCoalEvolution.brentOil.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>

              <LineChart
                title="Brent Oil M+1"
                yAxisTitle="$/bbl"
                showLegend={false}
                colors={[COLORS.red]}
                data={[...oilCoalData.chartBrentMoth]}
              />
              <ChartHints />
            </div>
          </div>
          <div className="col-md-6 mb-10" id="coal">
            <h2>
              <span>COAL</span>
            </h2>
            <div className="OilCoalSection__content">
              <div className="section-summary mb-4 pl-2-8">
                <Evolution
                  arrow={oilCoalData.coal.arrow}
                  price={oilCoalData.coal.value}
                  origin={oilCoalEvolution.coal.origin}
                  currency={oilCoalEvolution.coal.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>

              <LineChart
                title="Coal API2 CAL Y+1"
                yAxisTitle="$/tonne "
                colors={[COLORS.blue, COLORS.red, COLORS.orangeChart]}
                data={[...oilCoalData.chartCoal]}
              />
              <ChartHints />
            </div>
          </div>
        </div>

        <h2 id="carbon">
          <span>Carbon Certificates</span>
        </h2>
        <div className="row">
          <div className="col-md-6">
            <div className="OilCoalSection__content">
              <div className="section-summary mb-4 pl-2-8">
                <Evolution
                  arrow={oilCoalData.eua.arrow}
                  price={oilCoalData.eua.value}
                  origin={oilCoalEvolution.eua.origin}
                  currency={oilCoalEvolution.eua.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="OilCoalSection__content">
              <div className="section-summary mb-4 pl-2-8">
                <Evolution
                  arrow={oilCoalData.ukets.arrow}
                  price={oilCoalData.ukets.value}
                  origin={oilCoalEvolution.ukEts.origin}
                  currency={oilCoalEvolution.ukEts.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-10">
          <div className="col-md-6">
            <h3 className="pl-2-8">Carbon Certificates</h3>
            <div className="OilCoalSection__toggler">
              <Toggler
                leftText={togglerState.euro}
                rightText={togglerState.pound}
                handler={onToggle}
              />
            </div>
            {carbonUnits === togglerState.euro ? (
              <>
                <LineChart
                  yAxisTitle="€/tonne"
                  colors={[COLORS.blue, COLORS.red, COLORS.orangeChart]}
                  data={[...oilCoalData.chartCarbonEuro]}
                />
                <ChartHints />
              </>
            ) : (
              <>
                <LineChart
                  yAxisTitle="£/tonne"
                  colors={[COLORS.blue, COLORS.red, COLORS.orangeChart]}
                  data={[...oilCoalData.chartCarbonPound]}
                />
                <ChartHints />
              </>
            )}
          </div>
          <div className="col-md-6 pt-5">
            <EuaCertificatesTable
              data={{ ...oilCoalData.euaPricesTable }}
              onEditCell={onSave}
              onRemoveCustomPrice={onRemoveCustomPrice}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
